import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Car } from "lucide-react";
import { api } from "@/lib/api";

const fmtAUD = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);

export default function FeaturedVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);
  const [scrollState, setScrollState] = useState({ atStart: true, atEnd: false });

  useEffect(() => {
    api.get("/vehicles")
      .then((r) => setVehicles((r.data || []).slice(0, 6)))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, []);

  const updateScrollState = () => {
    const el = trackRef.current;
    if (!el) return;
    setScrollState({
      atStart: el.scrollLeft <= 8,
      atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8,
    });
  };

  useEffect(() => {
    updateScrollState();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [vehicles]);

  const scrollByCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-card]");
    const step = card ? card.offsetWidth + 20 : el.clientWidth * 0.85;
    el.scrollBy({ left: step * dir, behavior: "smooth" });
  };

  if (!loading && vehicles.length === 0) return null;

  return (
    <section
      data-testid="section-featured-vehicles"
      className="py-24 md:py-32 bg-[#080808] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div>
            <div className="label-eyebrow mb-4">Featured Fleet</div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-tight" style={{ fontWeight: 300 }}>
              A slice of the fleet, <span className="italic text-[#D4AF37]">ready to go.</span>
            </h2>
            <p className="text-white/60 mt-4 max-w-xl leading-relaxed">
              Brand-new 2026 stock — utes, SUVs and sedans, insured and registered. Swipe through, then jump into the full list.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scrollByCards(-1)}
              disabled={scrollState.atStart}
              data-testid="featured-vehicles-prev"
              aria-label="Previous vehicles"
              className="w-11 h-11 flex items-center justify-center border border-white/15 rounded-full text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => scrollByCards(1)}
              disabled={scrollState.atEnd}
              data-testid="featured-vehicles-next"
              aria-label="Next vehicles"
              className="w-11 h-11 flex items-center justify-center border border-white/15 rounded-full text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => <SkeletonCard key={i} />)}
          </div>
        ) : (
          <div
            ref={trackRef}
            data-testid="featured-vehicles-track"
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-2 -mx-6 md:-mx-12 px-6 md:px-12 scrollbar-hide"
            style={{ scrollbarWidth: "none" }}
          >
            {vehicles.map((v, i) => <VehicleCard key={v.id} v={v} index={i} />)}
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <Link
            to="/services"
            data-testid="featured-vehicles-see-all"
            className="group inline-flex items-center gap-2 border border-white/15 text-white px-6 py-3 text-xs tracking-widest hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition"
          >
            SEE THE FULL FLEET
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      <style>{`.scrollbar-hide::-webkit-scrollbar{display:none;}`}</style>
    </section>
  );
}

function VehicleCard({ v, index }) {
  return (
    <motion.div
      data-testid={`section-vehicle-card-${v.id}`}
      data-carousel-card
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: Math.min(index * 0.08, 0.4), ease: [0.22, 1, 0.36, 1] }}
      className="group snap-start shrink-0 w-[85%] sm:w-[52%] md:w-[calc((100%-40px)/3)] bg-[#0a0a0a] border border-white/10 hover:border-[#D4AF37]/40 transition-all duration-500 hover:shadow-[0_10px_40px_rgba(212,175,55,0.12)] overflow-hidden"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-white/5">
        {v.image ? (
          <img
            src={v.image}
            alt={`${v.year} ${v.make} ${v.model}`}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30"><Car size={40} /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-70" />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-[#D4AF37]/30 text-[10px] tracking-widest text-[#D4AF37]">
          {v.type?.toUpperCase() || "VEHICLE"}
        </div>
        <div className="absolute bottom-3 right-3 text-[10px] tracking-widest text-white/70 font-mono">
          {v.year}
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-display text-2xl text-white leading-tight" style={{ fontWeight: 400 }}>
          {v.make}
        </h3>
        <div className="text-white/60 text-sm mb-4">{v.model}</div>

        {v.highlights?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-5">
            {v.highlights.slice(0, 3).map((h) => (
              <span key={h} className="text-[10px] tracking-wide text-white/70 border border-white/10 rounded px-2 py-0.5">
                {h}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-end justify-between pt-4 border-t border-white/10">
          <div>
            <div className="text-[10px] tracking-widest text-white/40">FROM</div>
            <div className="font-display text-2xl text-[#D4AF37]" style={{ fontWeight: 500 }}>
              {fmtAUD(v.weekly_from)}
              <span className="text-xs text-white/50 ml-1">/wk</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-widest text-white/40">DRIVE AWAY</div>
            <div className="text-sm text-white/80">{fmtAUD(v.drive_away_price)}</div>
          </div>
        </div>

        <Link
          to="/services"
          data-testid={`section-vehicle-card-${v.id}-cta`}
          className="mt-5 group/link inline-flex items-center gap-1 text-xs tracking-widest text-white/70 hover:text-[#D4AF37] transition"
        >
          VIEW DETAILS
          <ArrowRight size={12} className="group-hover/link:translate-x-1 transition-transform" />
        </Link>
      </div>
    </motion.div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-[#0a0a0a] border border-white/10 overflow-hidden">
      <div className="aspect-[4/3] bg-white/5 animate-pulse" />
      <div className="p-6 space-y-3">
        <div className="h-5 w-2/3 bg-white/10 animate-pulse rounded" />
        <div className="h-3 w-1/2 bg-white/5 animate-pulse rounded" />
        <div className="h-8 w-1/3 bg-white/10 animate-pulse rounded mt-4" />
      </div>
    </div>
  );
}
